import React, { useCallback, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { getDeferredPayments } from "../api/protected";
import { getAccountUrl } from "../session";
import { PageLoader } from "../components/Loader";

const STATUS_LABELS = {
  pending: { text: "Ожидает", color: "#f0ad4e" },
  paid: { text: "Оплачен", color: "#28a745" },
  fiscalized: { text: "Фискализирован", color: "#17a2b8" },
  cancelled: { text: "Отменён", color: "#6c757d" },
  expired: { text: "Истёк", color: "#dc3545" },
};

const FILTERS = [
  { key: "pending", label: "Ожидают" },
  { key: "paid", label: "Оплачены" },
  { key: "", label: "Все" },
];

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatAmount = (kopecks) => {
  const sum = Math.round((kopecks || 0) / 100);
  return sum.toLocaleString("ru-RU") + " сум";
};

export default function DeferredPaymentsPage() {
  const accountUrl = (getAccountUrl() || "").trim();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [qrItem, setQrItem] = useState(null);

  const load = useCallback(async () => {
    if (!accountUrl) {
      setErr("Аккаунт не определён");
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const res = await getDeferredPayments(accountUrl, {
        status: statusFilter || undefined,
      });
      setItems(Array.isArray(res) ? res : res?.data ?? []);
    } catch (e) {
      setErr(e?.response?.data?.message ?? e?.message ?? "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [accountUrl, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const now = Date.now();

  return (
    <section
      style={{
        padding: "24px 28px",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      <div style={{ marginBottom: "20px" }}>
        <h1 style={{ fontSize: "22px", fontWeight: 700, margin: 0 }}>
          Отложенные UzQR
        </h1>
        <p style={{ fontSize: "13px", color: "#666", marginTop: "4px" }}>
          {loading ? "Загрузка..." : `${items.length} записей`}
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "16px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setStatusFilter(f.key)}
            style={{
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: statusFilter === f.key ? 700 : 500,
              border: `1px solid ${statusFilter === f.key ? "#3c8490" : "#ddd"}`,
              background: statusFilter === f.key ? "rgba(60,132,144,0.1)" : "#fff",
              color: statusFilter === f.key ? "#3c8490" : "#555",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={load}
          style={{
            marginLeft: "auto",
            padding: "8px 14px",
            fontSize: "13px",
            border: "1px solid #ddd",
            background: "#fff",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          ⟳ Обновить
        </button>
      </div>

      {err && (
        <div
          style={{
            padding: "12px",
            marginBottom: "16px",
            background: "#f8d7da",
            color: "#721c24",
            borderRadius: "6px",
          }}
        >
          {err}
        </div>
      )}

      {loading ? (
        <PageLoader />
      ) : items.length === 0 ? (
        <div
          style={{
            padding: "40px",
            textAlign: "center",
            color: "#999",
            border: "1px dashed #ddd",
            borderRadius: "8px",
          }}
        >
          Нет отложенных платежей
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {items.map((item) => {
            const st = STATUS_LABELS[item.status] || { text: item.status, color: "#666" };
            const expired = item.expires_at && new Date(item.expires_at).getTime() < now;
            return (
              <div
                key={item._id}
                style={{
                  padding: "14px 16px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  background: "#fff",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "8px" }}>
                  <div>
                    <div style={{ fontSize: "15px", fontWeight: 600 }}>
                      Заказ #{item.order_name}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                      {formatDateTime(item.createdAt)}
                      {item.cashier_name ? ` · ${item.cashier_name}` : ""}
                      {" · планшет "}
                      {item.tablet_id}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "16px", fontWeight: 700 }}>
                      {formatAmount(item.amount)}
                    </div>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: st.color, marginTop: "2px" }}>
                      {st.text}
                      {expired && item.status === "pending" ? " · QR истёк" : ""}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
                  <button
                    onClick={() => setQrItem(item)}
                    style={{
                      padding: "6px 14px",
                      fontSize: "13px",
                      border: "1px solid #3c8490",
                      background: "#fff",
                      color: "#3c8490",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    Показать QR
                  </button>
                </div>

                {item.transaction_id && (
                  <div style={{ fontSize: "11px", color: "#999", marginTop: "8px", fontFamily: "monospace" }}>
                    tx: {item.transaction_id}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {qrItem && (
        <div
          onClick={() => setQrItem(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              padding: "24px",
              borderRadius: "12px",
              textAlign: "center",
              maxWidth: "340px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ fontSize: "15px", fontWeight: 600, marginBottom: "6px" }}>
              Заказ #{qrItem.order_name}
            </div>
            <div style={{ fontSize: "13px", color: "#666", marginBottom: "16px" }}>
              {formatAmount(qrItem.amount)}
            </div>
            <div style={{ padding: "12px", background: "#fff", display: "inline-block", border: "1px solid #eee" }}>
              <QRCode value={qrItem.qr_payload} size={220} />
            </div>
            <div style={{ marginTop: "16px" }}>
              <button
                onClick={() => setQrItem(null)}
                style={{
                  padding: "8px 24px",
                  fontSize: "13px",
                  border: "1px solid #ddd",
                  background: "#fff",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
